import React from 'react';
import { ArrowLeft } from 'lucide-react';

const IndoorCaptureGuidePage: React.FC = () => {
  const handleBack = () => {
    // Navigation logic would be handled by your app's routing
    console.log('Navigate back');
  };
  
  return (
    <div className="min-h-screen bg-[#161513] py-12 px-4 sm:px-6">
      {/* Logo Background */}
      <div 
        className="absolute top-0 left-0 right-0 h-[400px] bg-cover bg-center bg-no-repeat opacity-20 z-0 pointer-events-none"
        style={{ backgroundImage: 'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMjAgODBMNTAgMjBMODAgODBaIiBmaWxsPSIjMDg3OUEzIiBmaWxsLW9wYWNpdHk9IjAuMyIvPjwvc3ZnPg==)' }}
      />
      
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white">3D Scanning Guide</h1>
          
          <button 
            onClick={handleBack} 
            className="flex items-center text-gray-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            Back
          </button>
        </div>
        
        <div className="bg-[#161513] rounded-xl shadow-lg border border-[#0CB4E4]/30 overflow-hidden">
          <div className="p-6 md:p-8 border-b border-[#0CB4E4]/20">
            <p className="text-gray-300 mt-2">A step-by-step guide for capturing high-quality 3D reconstructions</p>
          </div>
          
          <div className="p-6 md:p-8">
            <div className="prose prose-invert prose-blue max-w-none">
              <h2 className="text-xl font-semibold text-white mt-6 mb-3">
                Preparation
              </h2>

              <ol className="list-decimal pl-5 space-y-6 text-gray-300">
                <li>
                  <strong className="text-white">Set up the environment</strong>
                  <ul className="list-disc pl-5 mt-2 space-y-2">
                    <li>
                      <strong className="text-[#0CB4E4]">Capture on a cloudy day</strong>  or a time with ample natural light, ideally when the sun isn't shining directly into the space to reduce shadows.
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        
                        <li>If indoor, turn on all lights. Open all connecting doors and window blinds.</li>
                        <li>Avoid standing in front of light sources to prevent casting shadows.</li>
                      </ul>
                    </li>
                    <li>
                      <strong className="text-[#0CB4E4]">Remove all motion:</strong> When possible, clear the space of people, pets, and anything that might move, including <strong>unsteady</strong> items like spinning chairs or loose curtains. If you do get some movement in your shots don't worry - our AI will automatically removing people, cars or objects in motion.
                    </li>
                  </ul>
                </li>

                <li>
                  <strong className="text-white">Set up your camera</strong>
                  <ul className="list-disc pl-5 mt-2 space-y-2">
                    <li>
                      <strong className="text-[#0CB4E4]">If using our iPhone app:</strong> set the camera to <strong className="text-[#0CB4E4]">landscape mode</strong> at all times. This ensures &gt;70% overlap between photos, which is essential for generating an accurate 3D spatial model.
                    </li>
                    <li>
                     <strong className="text-[#0CB4E4]"> Use ultra-wide Lens</strong> if using other cameras:
                      <div className="overflow-x-auto mt-2">
                        <table className="min-w-full bg-[#161513] border border-[#0CB4E4]/30 rounded-lg">
                          <thead>
                            <tr>
                              <th className="px-4 py-2 border-b border-r border-[#0CB4E4]/30 text-left text-sm text-white">Camera Type</th>
                              <th className="px-4 py-2 border-b border-r border-[#0CB4E4]/30 text-left text-sm text-white">Sensor Size</th>
                              <th className="px-4 py-2 border-b border-[#0CB4E4]/30 text-left text-sm text-white">Lens Focal Length</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="px-4 py-2 border-b border-r border-[#0CB4E4]/30 text-sm text-gray-300">DSLR</td>
                              <td className="px-4 py-2 border-b border-r border-[#0CB4E4]/30 text-sm text-gray-300">Full-frame</td>
                              <td className="px-4 py-2 border-b border-[#0CB4E4]/30 text-sm text-gray-300">≤ 13mm</td>
                            </tr>
                            <tr>
                              <td className="px-4 py-2 border-b border-r border-[#0CB4E4]/30 text-sm text-gray-300">DSLR</td>
                              <td className="px-4 py-2 border-b border-r border-[#0CB4E4]/30 text-sm text-gray-300">APS-C</td>
                              <td className="px-4 py-2 border-b border-[#0CB4E4]/30 text-sm text-gray-300">≤ 9mm</td>
                            </tr>
                            <tr>
                              <td className="px-4 py-2 border-b border-r border-[#0CB4E4]/30 text-sm text-gray-300">Mirrorless</td>
                              <td className="px-4 py-2 border-b border-r border-[#0CB4E4]/30 text-sm text-gray-300">Full-frame</td>
                              <td className="px-4 py-2 border-b border-[#0CB4E4]/30 text-sm text-gray-300">≤ 13mm</td>
                            </tr>
                            <tr>
                              <td className="px-4 py-2 border-r border-[#0CB4E4]/30 text-sm text-gray-300">Mirrorless</td>
                              <td className="px-4 py-2 border-r border-[#0CB4E4]/30 text-sm text-gray-300">APS-C</td>
                              <td className="px-4 py-2 text-sm text-gray-300">≤ 9mm</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </li>
                    <li>Capture <strong className="text-[#0CB4E4]">still photos</strong>, not video, to reduce motion blur.</li>
                    <li>Ensure your camera lens is clean.</li>
                    <li>Keep the camera steady to avoid motion blur—use a stabilizer if available, especially for cameras without strong optical stabilization.</li>
                  </ul>
                </li>

                <li>
                  <strong className="text-white">Plan your scan path</strong>
                  <ul className="list-disc pl-5 mt-2 space-y-2">
                    <li>Scan the entire space in one session to maintain consistent lighting. Capture all relevant areas—<strong>indoors</strong>, <strong>outdoors</strong>, or a mix—within the same session.</li>
                    <li>Review the layout beforehand to plan your path and identify areas that need special attention <strong>so you can</strong> avoid missing sections.</li>
                  </ul>
                </li>
              </ol>

              <hr className="border-[#0CB4E4]/20 my-8" />

              <h2 className="text-xl font-semibold text-white mt-6 mb-3">
                Capture Process
              </h2>

              <ol className="list-decimal pl-5 space-y-6 text-gray-300">
                <li>
                  <strong className="text-white">Scan the perimeter</strong>
                  <ul className="list-disc pl-5 mt-2 space-y-2">
                    <li>Start at the corner of the space, consistently keeping <strong className="text-[#0CB4E4]">your back against the background</strong> (or wall for indoors), facing the center of the space.</li>
                    <li>
                      Take one picture, then rotate slightly and take another, continuing until <strong className="text-[#0CB4E4]">you've covered 180°</strong> around you. Repeat this at each of the three heights. It will feel like you are taking a panorama at each height:
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li><strong className="text-[#0CB4E4]">Head height:</strong> camera/phone tilted up ~45°</li>
                        <li><strong className="text-[#0CB4E4]">Chest height:</strong> camera/phone straight out</li>
                        <li><strong className="text-[#0CB4E4]">Waist/knee height:</strong> camera/phone tilted down ~45°</li>
                      </ul>
                    </li>
                    <li>Step sideways <strong>3–9 ft (1–3 meters)</strong> between shots to ensure ~70% overlap.</li>
                  </ul>
                </li>

                <li>
                  <strong className="text-white">Take pictures of each background detail and then orbit each object in the space</strong>
                  <ul className="list-disc pl-5 mt-2 space-y-2">
                    <li>After scanning the perimeter, move closer to key items (e.g., face wall features, orbit furniture &amp; decor).</li>
                    <li>Capture from multiple elevations: head, chest, waist, and other useful angles.</li>
                    <li><strong>Tip:</strong> Maintain &gt;70% overlap again by stepping no more than 3 ft (1 meter) when moving between items. Don't get too close: Orbit each item from at least 3 ft (1 meter) for large items like furniture or <strong>12 in. (30 cm)</strong> from small ones like a cup.</li>
                  </ul>

                  <p className="text-gray-300 mt-3">
                    Complete <strong>Steps 1 and 2</strong> for each space or room before moving to the next.
                  </p>
                </li>
              </ol>

              <hr className="border-[#0CB4E4]/20 my-8" />

              <h2 className="text-xl font-semibold text-white mt-6 mb-3">
                Multi-spaces captures
              </h2>

              <div className="text-gray-300 space-y-4">
                <p>
                  In any <strong>connecting space</strong> (like hallways, doorways, or corridors), you should make <strong>two separate passes</strong>:
                </p>

                <ul className="list-disc pl-5 space-y-2">
                  <li>You'll first walk through the space taking pictures while facing forward, then walk back taking pictures while facing the opposite direction (backward).</li>
                  <li>For each pass, capture 180° at each elevation following step 1b.</li>
                </ul>
<div className="mt-8 bg-[#0CB4E4]/10 p-4 rounded-lg border border-[#0CB4E4]/30">
                <h3 className="text-lg font-medium text-white mb-3">Pro tips:</h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li><strong>Avoid capturing plain white walls</strong> in any shot—angle your camera to include parts of the space to help the system align images, especially in tight spaces, connecting hallways, or <strong>during</strong> indoor/outdoor transitions.</li>
                  <li><strong>Avoid capturing only the outdoor view through windows</strong>—angle <strong>the</strong> camera to include some interior framing in each window shot <strong>while</strong> showing the outdoor scene.</li>
                </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-6 md:p-8 bg-[#161513]/70 border-t border-[#0CB4E4]/20">
            <div className="flex items-center justify-between">
              <button 
                onClick={handleBack}
                className="px-5 py-2.5 bg-[#0C5484] text-white hover:bg-[#0CB4E4]/90 transition-colors shadow-md rounded-lg"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndoorCaptureGuidePage;